import { FC, useState, useCallback, useMemo, memo, useEffect, useRef } from 'react';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
    CheckCircle,
    AlertTriangle,
    Info,
    XCircle,
    Eye,
    Calendar,
    Bell,
    ChevronRight,
    Clock,
    Filter,
    Star,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { INotificationItem } from '../../../../app/modules/core/_models';
import { updateNotificationFlags } from '../../../../app/modules/core/_request';
import { NotificationDetailModal } from '../../../../app/modules/notification/acknowledgementpopup';
import { CustomDateFormate } from '../../../../utils/date';

export type NotificationFilter = 'all' | 'danger' | 'warning' | 'success' | 'info';

interface Props {
    onClose: () => void;
    onUnreadCountChange?: (count: number) => void;
    notificationData: INotificationItem[];
    loading: boolean;
    loadingMore: boolean;
    hasMore: boolean;
    fetchNotifications: (
        page: number,
        append: boolean,
        filterType: NotificationFilter,
        importantOnly: boolean
    ) => Promise<INotificationItem[]>;
    unreadCount: number;
    starredCount: number;
}

interface NotificationComponentProps {
    key: number;
    notification: INotificationItem;
    index: number;
    isHovered: boolean;
    onMouseEnter: (id: number) => void;
    onMouseLeave: () => void;
    onMarkAsRead: (id: number) => void;
    onToggleImportant: (id: number) => void;
    formatDateTime: (dateString: string) => { date: string; time: string };
    getTypeConfig: (type: string) => {
        icon: any;
        gradient: string;
        color: string;
        bg: string;
        border: string;
        glow: string;
    };
    getPriorityColor: (priority?: string) => string;
    loading: boolean;
    handleShowDetailModal: (data: INotificationItem) => void;
}

// Single source of truth for a query. Page reset is always atomic with other changes.
interface NotificationQuery {
    page: number;
    filterType: NotificationFilter;
    importantOnly: boolean;
}

// Constants
const TYPE_CONFIG = {
    success: {
        icon: CheckCircle,
        gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        color: '#10b981',
        bg: '#f0fdf4',
        border: '#10b981',
        glow: '0 0 12px rgba(16,185,129,0.2)',
    },
    warning: {
        icon: AlertTriangle,
        gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        color: '#f59e0b',
        bg: '#fffbeb',
        border: '#f59e0b',
        glow: '0 0 12px rgba(245,158,11,0.2)',
    },
    danger: {
        icon: XCircle,
        gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        color: '#ef4444',
        bg: '#fef2f2',
        border: '#ef4444',
        glow: '0 0 12px rgba(239,68,68,0.2)',
    },
    info: {
        icon: Info,
        gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
        color: '#3b82f6',
        bg: '#eff6ff',
        border: '#3b82f6',
        glow: '0 0 12px rgba(59,130,246,0.2)',
    },
};

const FILTERS: { id: NotificationFilter; label: string; gradient: string; color: string }[] = [
    { id: 'all', label: 'All', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#667eea' },
    { id: 'danger', label: '⚠ Danger', gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', color: '#ef4444' },
    { id: 'warning', label: '⚠ Warning', gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: '#f59e0b' },
    { id: 'success', label: '✓ Success', gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: '#10b981' },
    { id: 'info', label: 'ℹ Info', gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: '#3b82f6' },
];

// Memoized Notification Item Component
const NotificationItemComponent = memo(
    ({
        notification,
        index,
        isHovered,
        onMouseEnter,
        onMouseLeave,
        onMarkAsRead,
        onToggleImportant,
        formatDateTime,
        getTypeConfig,
        loading,
        handleShowDetailModal,
    }: NotificationComponentProps) => {
        const typeConfig = getTypeConfig(notification.notification_type);
        const TypeIcon = typeConfig.icon;
        const isUnread = !notification.is_read;
        const isImportant = notification.is_starred;

        return (
            <div
                className="menu-item px-2 mb-1"
                style={{
                    cursor: 'pointer',
                    animation: `fadeInUp 0.3s ease ${index * 0.03}s both`,
                }}
                onMouseEnter={() => onMouseEnter(notification.id)}
                onMouseLeave={onMouseLeave}
                onClick={(e) => {
                    e.stopPropagation();
                    handleShowDetailModal(notification);
                    onMarkAsRead(notification.id);
                }}
            >
                <div
                    className="menu-content p-2 rounded-3"
                    style={{
                        background: isUnread ? typeConfig.bg : isImportant ? '#fefce8' : 'white',
                        borderLeft: `3px solid ${isUnread ? typeConfig.border : isImportant ? '#eab308' : 'transparent'
                            }`,
                        boxShadow: isHovered ? '0 4px 12px rgba(0,0,0,0.08)' : 'none',
                        transition: 'all 0.2s ease',
                        transform: isHovered ? 'translateX(2px)' : 'translateX(0)',
                        opacity: loading ? 0.7 : 1,
                    }}
                >
                    <div className="d-flex gap-2">
                        <div className="position-relative">
                            <div
                                className="d-flex align-items-center justify-content-center rounded-circle p-1"
                                style={{
                                    width: '30px',
                                    height: '30px',
                                    background: typeConfig.gradient,
                                    boxShadow: typeConfig.glow,
                                }}
                            >
                                <TypeIcon size={18} className="text-white" />
                            </div>
                        </div>

                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                            <div className="d-flex align-items-center justify-content-between mb-0">
                                <div className="d-flex align-items-center gap-1 flex-wrap">
                                    <OverlayTrigger
                                        placement="bottom"
                                        trigger={['hover', 'focus']}
                                        container={document.body}
                                        overlay={
                                            <Tooltip id={`title-tooltip-${notification.id}`}>
                                                {notification.title}{' '}
                                                {notification.extra?.projectShortName &&
                                                    `- ${notification.extra.projectShortName}`}
                                            </Tooltip>
                                        }
                                    >
                                        <span className="fw-semibold fs-7">
                                            {notification.title?.length > 60
                                                ? `${notification.title.substring(0, 60)}...`
                                                : notification.title}{' '}
                                            {notification.extra?.projectShortName &&
                                                `- ${notification.extra.projectShortName}`}
                                        </span>
                                    </OverlayTrigger>
                                </div>
                            </div>

                            <OverlayTrigger
                                placement="bottom"
                                trigger={['hover', 'focus']}
                                container={document.body}
                                overlay={
                                    <Tooltip id={`message-tooltip-${notification.id}`}>
                                        {notification.message}
                                    </Tooltip>
                                }
                            >
                                <p
                                    className="mb-1 text-muted fs-8"
                                    style={{ whiteSpace: 'normal', cursor: 'pointer' }}
                                >
                                    {notification.message?.length > 60
                                        ? `${notification.message.substring(0, 60)}...`
                                        : notification.message}
                                </p>
                            </OverlayTrigger>

                            <div className="d-flex align-items-center gap-2 mt-1">
                                <div className="d-flex align-items-center gap-1">
                                    <Calendar size={10} className="text-muted" />
                                    <span
                                        className="text-muted fs-8"
                                        style={{ fontSize: '0.65rem', fontWeight: '500' }}
                                    >
                                        {formatDateTime(notification.creation_date).date}
                                    </span>
                                </div>
                                <div className="d-flex align-items-center gap-1">
                                    <Clock size={10} className="text-muted" />
                                    <span
                                        className="text-muted fs-8"
                                        style={{ fontSize: '0.65rem', fontWeight: '500' }}
                                    >
                                        {formatDateTime(notification.creation_date).time}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="d-flex flex-column align-items-end gap-3">
                            <div className="d-flex gap-1 align-items-start">
                                <OverlayTrigger
                                    placement="auto"
                                    overlay={
                                        <Tooltip id={`important-tooltip-${notification.id}`}>
                                            {isImportant ? 'Remove from important' : 'Mark as important'}
                                        </Tooltip>
                                    }
                                >
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onToggleImportant(notification.id);
                                        }}
                                        className="btn btn-sm rounded-circle d-flex align-items-center justify-content-center p-2"
                                        style={{ background: isImportant ? '#fef3c7' : '#f3f4f6' }}
                                        disabled={loading}
                                    >
                                        {isImportant ? (
                                            <Star size={12} fill="#eab308" color="#eab308" />
                                        ) : (
                                            <Star size={12} />
                                        )}
                                    </button>
                                </OverlayTrigger>
                                <OverlayTrigger
                                    placement="auto"
                                    overlay={
                                        <Tooltip id={`mark-read-tooltip-${notification.id}`}>
                                            Mark as read
                                        </Tooltip>
                                    }
                                >
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onMarkAsRead(notification.id);
                                        }}
                                        className="btn btn-sm rounded-circle d-flex align-items-center justify-content-center p-2"
                                        style={{ background: '#e3f2fd' }}
                                        disabled={loading}
                                    >
                                        <Eye size={12} />
                                    </button>
                                </OverlayTrigger>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
);

NotificationItemComponent.displayName = 'NotificationItemComponent';

const Notification: FC<Props> = ({
    onClose,
    onUnreadCountChange,
    notificationData: initialNotificationData,
    loading,
    loadingMore,
    hasMore,
    fetchNotifications,
    unreadCount,
    starredCount,
}) => {
    const navigate = useNavigate();

    // Local list. Populated from parent + local fetches (filter changes, pagination).
    const [notificationData, setNotificationData] = useState<INotificationItem[]>(
        initialNotificationData
    );

    // Single atomic query: any filter change resets page in the same update.
    const [query, setQuery] = useState<NotificationQuery>({
        page: 1,
        filterType: 'all',
        importantOnly: false,
    });

    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState<INotificationItem | null>(null);
    const [hoveredId, setHoveredId] = useState<number | null>(null);
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [mutationLoading, setMutationLoading] = useState(false);

    // Track whether the query effect has run at least once so we don't
    // overwrite the initial data on mount.
    const isFirstQueryRun = useRef(true);

    // Keep parent-provided data in sync when the parent refreshes.
    useEffect(() => {
        setNotificationData(initialNotificationData);
    }, [initialNotificationData]);

    // Single owner of fetch. Runs on every query change (page, filters, importantOnly).
    // Uses a cancel token to avoid stale responses overwriting newer ones.
    useEffect(() => {
        if (isFirstQueryRun.current) {
            isFirstQueryRun.current = false;
            return;
        }

        let cancelled = false;
        const append = query.page > 1;

        (async () => {
            try {
                const list = await fetchNotifications(
                    query.page,
                    append,
                    query.filterType,
                    query.importantOnly
                );
                if (cancelled) return;
                setNotificationData((prev) => (append ? [...prev, ...list] : list));
            } catch (err) {
                if (!cancelled) console.error('Failed to fetch notifications:', err);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [fetchNotifications, query]);

    // Unread count derived from local data — no side-effect inside setState updater.
    const localUnreadCount = useMemo(
        () => notificationData.filter((n) => !n.is_read).length,
        [notificationData]
    );

    useEffect(() => {
        onUnreadCountChange?.(localUnreadCount);
    }, [localUnreadCount, onUnreadCountChange]);

    // API call handler — one setState, pure updater, side effects lifted out.
    const handleApiCall = useCallback(
        async (ids: number[], updates: Partial<INotificationItem>) => {
            if (!ids.length) return;

            setMutationLoading(true);
            try {
                const payload: any = { ids };
                Object.keys(updates).forEach((key) => {
                    payload[key] = updates[key as keyof INotificationItem];
                });

                const response = await updateNotificationFlags(payload);

                if (response.status === 'ok') {
                    setNotificationData((previous) =>
                        previous.map((item) =>
                            ids.includes(item.id) ? { ...item, ...updates } : item
                        )
                    );
                } else {
                    console.error('Update failed:', response.message);
                }
            } catch (error) {
                console.error('Error updating notifications:', error);
            } finally {
                setMutationLoading(false);
            }
        },
        []
    );

    // Memoized configuration functions
    const getTypeConfig = useCallback(
        (type: string) =>
            TYPE_CONFIG[type?.toLowerCase() as keyof typeof TYPE_CONFIG] || TYPE_CONFIG.info,
        []
    );

    const getPriorityColor = useCallback((priority?: string) => {
        switch (priority?.toLowerCase()) {
            case 'high':
                return '#ef4444';
            case 'medium':
                return '#f59e0b';
            case 'low':
                return '#10b981';
            default:
                return '#6c757d';
        }
    }, []);

    const formatDateTime = useCallback((dateString: string) => {
        if (!dateString) return { date: 'Unknown', time: '' };

        const date = new Date(dateString);
        const now = new Date();
        const diffMins = Math.floor((now.getTime() - date.getTime()) / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);

        const time = date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        });

        let dateStr;
        if (diffMins < 1) dateStr = 'Just now';
        else if (diffMins < 60) dateStr = `${diffMins} min ago`;
        else if (diffHours < 24) dateStr = `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
        else if (diffDays < 7) dateStr = `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
        else dateStr = CustomDateFormate(date);

        return { date: dateStr, time };
    }, []);

    // Handlers with API integration
    const handleMarkAsRead = useCallback(
        (id: number) => {
            handleApiCall([id], { is_read: true });
        },
        [handleApiCall]
    );

    const handleToggleImportant = useCallback(
        (id: number) => {
            const notification = notificationData.find((n) => n.id === id);
            if (notification) {
                handleApiCall([id], { is_starred: !notification.is_starred });
            }
        },
        [handleApiCall, notificationData]
    );

    // UI Handlers
    const handleMouseEnter = useCallback((id: number) => setHoveredId(id), []);
    const handleMouseLeave = useCallback(() => setHoveredId(null), []);

    // Atomic: filter change + page reset in one update.
    const handleFilterChange = useCallback((type: NotificationFilter) => {
        setQuery((q) => ({ ...q, filterType: type, page: 1 }));
    }, []);

    const handleToggleFilters = useCallback(() => setShowFilters((prev) => !prev), []);

    // Atomic: important toggle + page reset in one update.
    const handleToggleImportantOnly = useCallback(() => {
        setQuery((q) => ({ ...q, importantOnly: !q.importantOnly, page: 1 }));
    }, []);

    // Pagination: only page changes, no filter reset.
    const handleLoadMore = useCallback(() => {
        if (loading || loadingMore || !hasMore) return;
        setQuery((q) => ({ ...q, page: q.page + 1 }));
    }, [loading, loadingMore, hasMore]);

    const handleClearFilters = useCallback(() => {
        setQuery((q) => ({ ...q, filterType: 'all', importantOnly: false, page: 1 }));
    }, []);

    const handleNavigate = useCallback(() => {
        navigate('/manage-notifications');
        onClose();
    }, [navigate, onClose]);

    const handleClick = useCallback((e: React.MouseEvent) => e.stopPropagation(), []);

    const handleShowDetailModal = useCallback(
        (notification: INotificationItem) => {
            setSelectedNotification({ ...notification, is_read: true });
            setShowDetailModal(true);
            onClose();
        },
        [onClose]
    );

    const handleCloseDetailModal = useCallback(() => {
        setShowDetailModal(false);
        setSelectedNotification(null);
    }, []);

    // Memoized filter chips
    const filterChips = useMemo(
        () =>
            FILTERS.map((filter) => (
                <button
                    key={filter.id}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleFilterChange(filter.id);
                    }}
                    className="btn btn-sm rounded-pill px-3 py-1"
                    style={{
                        background:
                            query.filterType === filter.id ? filter.gradient : '#f3f4f6',
                        color: query.filterType === filter.id ? 'white' : filter.color,
                        border: 'none',
                        fontSize: '0.7rem',
                        fontWeight: '500',
                        transition: 'all 0.2s',
                    }}
                >
                    {filter.label}
                </button>
            )),
        [query.filterType, handleFilterChange]
    );

    return (
        <div
            className="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg menu-state-primary bg-white fw-semibold py-2 mt-5 mt-sm-3 w-100 w-sm-400px rounded-3 shadow-sm"
            style={{ position: 'relative', zIndex: 1050 }}
            data-kt-menu="true"
            onClick={handleClick}
        >
            {/* Header */}
            <div className="menu-item px-3 mb-2" onClick={handleClick}>
                <div className="d-flex align-items-center justify-content-between w-100">
                    <div className="d-flex align-items-center gap-2">
                        <div
                            className="rounded-circle d-flex align-items-center justify-content-center"
                            style={{
                                width: '32px',
                                height: '32px',
                                background:
                                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                boxShadow: '0 4px 10px rgba(102,126,234,0.25)',
                            }}
                        >
                            <Bell size={16} className="text-white" />
                        </div>
                        <div>
                            <span className="fw-bold fs-6">Notifications</span>
                            {unreadCount > 0 && (
                                <span className="badge bg-primary-light rounded-pill ms-2 fs-8 fw-semibold">
                                    {unreadCount} New
                                </span>
                            )}
                            {starredCount > 0 && (
                                <span className="badge bg-warning-light rounded-pill ms-1 fs-8 fw-semibold">
                                    <Star size={10} fill="#eab308" color="#eab308" /> {starredCount}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="d-flex gap-1">
                        <OverlayTrigger
                            placement="auto"
                            overlay={
                                <Tooltip id="important-filter-tooltip">
                                    Show important only
                                </Tooltip>
                            }
                        >
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleImportantOnly();
                                }}
                                className={`btn btn-sm rounded-pill d-flex align-items-center gap-1 p-2 ${query.importantOnly ? 'bg-light-warning' : 'bg-light'
                                    }`}
                                style={query.importantOnly ? { background: '#fef3c7' } : {}}
                                disabled={loading}
                            >
                                <Star
                                    size={14}
                                    className={query.importantOnly ? 'text-warning' : ''}
                                />
                            </button>
                        </OverlayTrigger>

                        <OverlayTrigger
                            placement="auto"
                            overlay={
                                <Tooltip id="notification-filter-tooltip">
                                    Filter Notifications
                                </Tooltip>
                            }
                        >
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleFilters();
                                }}
                                className={`btn btn-sm rounded-pill d-flex align-items-center gap-1 bg-light p-2 ${showFilters ? 'bg-light-primary text-primary' : ''
                                    }`}
                                disabled={loading}
                            >
                                <Filter size={14} />
                            </button>
                        </OverlayTrigger>
                    </div>
                </div>
            </div>

            {/* Filter Chips */}
            {showFilters && (
                <div className="menu-item px-3 mb-2" onClick={handleClick}>
                    <div className="d-flex gap-2 flex-wrap">{filterChips}</div>
                </div>
            )}

            <div className="separator my-1 opacity-50" />

            {/* Notification List */}
            <div
                className="menu-inner flex-column d-flex"
                style={{ maxHeight: '450px', overflowY: 'auto', overflowX: 'hidden' }}
            >
                {loading && (
                    <div className="text-center py-3">
                        <div className="spinner-border spinner-border-sm text-primary" />
                    </div>
                )}

                {!loading && notificationData.length !== 0 ? (
                    notificationData.map((notification, index) => (
                        <NotificationItemComponent
                            key={notification.id}
                            notification={notification}
                            index={index}
                            isHovered={hoveredId === notification.id}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                            onMarkAsRead={handleMarkAsRead}
                            onToggleImportant={handleToggleImportant}
                            formatDateTime={formatDateTime}
                            getTypeConfig={getTypeConfig}
                            getPriorityColor={getPriorityColor}
                            loading={loading || mutationLoading}
                            handleShowDetailModal={handleShowDetailModal}
                        />
                    ))
                ) : (
                    !loading && (
                        <div
                            className="menu-item justify-content-center py-4"
                            onClick={handleClick}
                        >
                            <div className="text-center">
                                <Bell size={32} className="text-muted opacity-50 mb-2" />
                                <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                                    {query.importantOnly
                                        ? 'No important notifications'
                                        : 'No new notifications'}
                                </p>
                                {(query.filterType !== 'all' || query.importantOnly) && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleClearFilters();
                                        }}
                                        className="btn btn-link btn-sm mt-2"
                                        style={{ fontSize: '0.7rem' }}
                                    >
                                        Clear filters
                                    </button>
                                )}
                            </div>
                        </div>
                    )
                )}
            </div>

            {hasMore && !loading && (
                <div className="menu-item px-5 py-2" onClick={handleClick}>
                    <Button
                        variant="light"
                        size="sm"
                        className="w-100"
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                    >
                        {loadingMore ? (
                            <span className="spinner-border spinner-border-sm me-2" />
                        ) : null}
                        {loadingMore ? 'Loading...' : 'Load More'}
                    </Button>
                </div>
            )}

            <div className="separator my-1 opacity-50" />

            {/* Footer */}
            <div className="menu-item px-3 mt-1 mb-1" onClick={handleClick}>
                <Button
                    variant="link"
                    size="sm"
                    className="w-100 text-decoration-none d-flex align-items-center justify-content-center gap-1"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleNavigate();
                    }}
                    style={{ fontSize: '0.75rem', fontWeight: '600', color: '#667eea' }}
                >
                    View All Notifications
                    <ChevronRight size={12} />
                </Button>
            </div>

            <NotificationDetailModal
                show={showDetailModal}
                onHide={handleCloseDetailModal}
                notification={selectedNotification}
                setSelectedNotification={setSelectedNotification}
            />

            <style>{`
                .tooltip-inner { max-width: 400px; white-space: normal; }
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .btn-sm { transition: all 0.2s ease; }
                .menu-item .btn-sm:hover { transform: scale(1.05); }
                .menu, .menu-sub, .menu-sub-dropdown { pointer-events: auto !important; }
                .bg-warning-light { background: #fef3c7; color: #d97706; }
            `}</style>
        </div>
    );
};

export { Notification };